<template>
  <section class="flex flex-col gap-3" data-testid="txn-analytics-regions">
    <div class="flex flex-wrap items-end justify-between gap-x-6 gap-y-2">
      <div>
        <h4 class="text-lg font-medium leading-tight">{{ t('messages.transactions.analytics.regions.title') }}</h4>
        <p class="text-sm text-gray-600">{{ caption }}</p>
      </div>
      <Button
        size="small"
        icon="pi pi-download"
        :label="t('messages.transactions.analytics.regions.export')"
        :loading="store.exportingCsv"
        :disabled="store.regionsState !== 'READY'"
        data-testid="txn-analytics-export-csv"
        @click="store.exportCsv()"
      />
    </div>

    <Message v-if="store.csvFailed" severity="error" size="small" :closable="false" data-testid="txn-analytics-export-csv-failed">
      {{ t('messages.transactions.analytics.regions.export_failed') }}
    </Message>

    <Message
      v-if="store.regionsState === 'ERROR'"
      severity="error"
      size="small"
      :closable="false"
      data-testid="txn-analytics-regions-failed"
    >
      <div class="flex flex-wrap items-center gap-3">
        <span>{{ store.regionsError ?? t('messages.transactions.analytics.regions.failed') }}</span>
        <Button
          size="small"
          severity="secondary"
          text
          :label="t('messages.transactions.analytics.state.retry')"
          @click="store.loadRegions()"
        />
      </div>
    </Message>

    <div v-else class="overflow-x-auto">
      <DataTable
        v-model:expandedRows="expandedRows"
        :value="rows"
        size="small"
        showGridlines
        scrollable
        dataKey="key"
        :rowClass="rowClass"
        :dt="COMPACT_TABLE_DT"
        :pt="EXPANSION_PT"
        class="text-sm"
        tableClass="min-w-[760px]"
        data-testid="txn-analytics-regions-table"
      >
        <Column expander style="width: 3rem" />
        <Column field="location" :header="t('messages.transactions.analytics.regions.region')">
          <template #body="{ data }">
            <Skeleton v-if="loading" width="9rem" height="1rem" />
            <!-- A long label ("Harju maakond · Tallinn · Põhja-Tallinna linnaosa") wraps between its levels, never inside one, and the tag drops under it. -->
            <div v-else class="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>
                <template v-for="(level, index) in data.levels" :key="index">{{ index ? ' · ' : '' }}<span class="whitespace-nowrap">{{ level }}</span></template>
              </span>
              <Tag
                v-if="data.tag"
                severity="secondary"
                :value="data.tag"
                class="text-[0.65rem] font-normal whitespace-nowrap"
                data-testid="txn-analytics-region-tag"
              />
            </div>
          </template>
        </Column>
        <Column field="count" :header="t('messages.transactions.analytics.table.n')" :pt="rightPT">
          <template #body="{ data }">
            <Skeleton v-if="loading" width="3rem" height="1rem" class="ml-auto" />
            <span v-else class="tabular-nums">{{ data.total ? formatEt(data.total.count, 0) : '–' }}</span>
          </template>
        </Column>
        <Column v-for="column in valueColumns" :key="column.field" :field="column.field" :header="column.header" :pt="rightPT">
          <template #body="{ data }">
            <Skeleton v-if="loading" width="4rem" height="1rem" class="ml-auto" />
            <span v-else class="tabular-nums">{{ data.total ? formatCell(data.total, column.field, column.dp) : '–' }}</span>
          </template>
        </Column>
        <Column v-if="multi" field="change" :header="t('messages.transactions.analytics.table.change')" :pt="rightPT">
          <template #body="{ data }">
            <Skeleton v-if="loading" width="3rem" height="1rem" class="ml-auto" />
            <span v-else class="tabular-nums whitespace-nowrap">{{ changeText(data.change) }}</span>
          </template>
        </Column>

        <template #expansion="{ data }">
          <div class="p-2 bg-surface-50">
            <PeriodComparisonTable
              v-if="data.hasTable && data.result.periods.length > 1"
              :periods="data.result.periods"
              :table="data.result.table"
            />
            <PriceStatisticsTable
              v-else-if="data.hasTable"
              :period="data.result.periods[0]"
              :table="data.result.table"
              :columns="data.result.columns"
            />
            <p v-else class="text-sm text-gray-500 px-2 py-1">
              {{ t('messages.transactions.analytics.regions.no_table') }}
            </p>
          </div>
        </template>
      </DataTable>
    </div>
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAnalyticsStore } from '@/stores/analyticsStore'
import { COMPACT_TABLE_DT, EXPANSION_PT, tableLayout } from '@/services/analyticsTable'
import { formatCell, formatEt } from '@/services/numberFormat'
import PeriodComparisonTable from './PeriodComparisonTable.vue'
import PriceStatisticsTable from './PriceStatisticsTable.vue'

/**
 * RED-370 (Coop) — "Piirkondade kaupa": with 2+ regions picked, each region
 * on its own under the combined result. One row per region with its KOKKU
 * (the newest period's in a comparison), the same headline cells as the
 * period comparison table; expanding a row shows the region's own table.
 * A region without a table says why in a tag. The CSV download carries every
 * row of every region (bands, periods). Mounted only when the store says the
 * result covers 2+ regions, so one region leaves the drawer as it was.
 */
const { t } = useI18n()
const store = useAnalyticsStore()

const OVERVIEW_FIELDS = ['area_mean', 'sum_total', 'ppm_median', 'ppm_mean', 'ppm_sd']

const expandedRows = ref({})
watch(
  () => store.regions,
  () => {
    expandedRows.value = {}
  },
)

const loading = computed(() => store.regionsState === 'LOADING' || store.regionsState === 'IDLE')
const layout = computed(() => tableLayout(store.result?.table, store.result?.columns))
const multi = computed(() => (store.result?.periods?.length ?? 0) > 1)

const caption = computed(() => {
  const periods = store.result?.periods ?? []
  return multi.value
    ? t('messages.transactions.analytics.regions.caption_latest', { period: periods[periods.length - 1]?.label ?? '' })
    : t('messages.transactions.analytics.regions.caption')
})

/** Headline cells, labelled with the result's units (€/m², €/ha, or € for a deal-sum report). */
const valueColumns = computed(() =>
  layout.value.valueColumns
    .filter(column => OVERVIEW_FIELDS.includes(column.field))
    .map(column => ({ ...column, header: headerOf(column) })),
)

const headerOf = column => {
  if (column.field === 'area_mean') {
    return t('messages.transactions.analytics.table.area_mean_in', { unit: layout.value.areaUnit })
  }
  if (column.field === 'sum_total') return t('messages.transactions.analytics.table.sum_total')
  return t('messages.transactions.analytics.regions.with_unit', {
    label: t(`messages.transactions.analytics.table.${column.leafKey}`),
    unit: column.group === 'unit' ? `€/${layout.value.priceUnit}` : '€',
  })
}

const SKELETON_ROWS = Array.from({ length: 3 }, (_, index) => ({ key: `skeleton-${index}` }))

const tagOf = (result, total) => {
  if (result?.state === 'EMPTY') return t('messages.transactions.analytics.regions.empty')
  if (result?.state === 'TOO_MANY') return t('messages.transactions.analytics.regions.too_many')
  if (total?.suppressed) return t('messages.transactions.analytics.table.suppressed')
  return null
}

const rows = computed(() => {
  if (loading.value) return SKELETON_ROWS
  return (store.regions ?? []).map((region, index) => {
    const result = region.result ?? {}
    const periods = result.periods ?? []
    const latest = periods[periods.length - 1] ?? null
    return {
      key: `${index}`,
      location: region.location,
      levels: String(region.location ?? '').split(' · '),
      result,
      total: latest?.total ?? null,
      change: latest?.change_pct_median ?? null,
      tag: tagOf(result, latest?.total),
      hasTable: (result.state === 'RESULT' || result.state === 'RESULT_PARTIAL') && periods.length > 0,
    }
  })
})

const rowClass = data => (data.tag ? 'text-gray-400' : '')

const changeText = value => {
  if (value === null || value === undefined) return '–'
  const arrow = value > 0 ? '▲ ' : value < 0 ? '▼ ' : ''
  return `${arrow}${formatEt(Math.abs(value), 1)} %`
}

const rightPT = {
  columnHeaderContent: { class: 'justify-end text-right' },
  bodyCell: { class: 'text-right whitespace-nowrap' },
}
</script>
